import { useQuery } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { useDebouncedValue } from "../../../lib/useDebouncedValue";
import { useAuth } from "../../../providers/AuthProvider";

export type DealerRecommendationRow = {
  dealer_id: number;
  name: string;
  distance_km: number | null;
  score: number;
  insights: string[];
};

function normalizeItemsKey(items: Array<{ product_id: string; quantity?: string }>) {
  const ids = items
    .map((it) => Number(it.product_id))
    .filter((n) => Number.isFinite(n) && n > 0);
  ids.sort((a, b) => a - b);
  return ids.join(",");
}

export function useDealerRecommendations(opts: {
  items: Array<{ product_id: string; quantity?: string }>;
  lat?: number | null;
  lng?: number | null;
}) {
  const { token } = useAuth();
  const itemsKey = normalizeItemsKey(opts.items);
  const debouncedItemsKey = useDebouncedValue(itemsKey, 300);

  return useQuery({
    queryKey: ["dealer-recommendations", debouncedItemsKey, opts.lat ?? null, opts.lng ?? null],
    enabled: Boolean(token && debouncedItemsKey),
    queryFn: async () => {
      if (!token) throw new Error("Missing auth token");
      const productIds = debouncedItemsKey.split(",").filter(Boolean);
      const res = await api.dealers.recommendations({
        token,
        payload: {
          items: productIds.map((pid) => ({ product_id: Number(pid), quantity: null })),
          lat: opts.lat ?? null,
          lng: opts.lng ?? null,
        },
      });
      return res.data as DealerRecommendationRow[];
    },
    staleTime: 2 * 60_000,
  });
}

